import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

import { getAdminOverview, createBusinessInvite } from '../../util/api';
import { storableError } from '../../util/errors';
import { fetchCurrentUser } from '../../ducks/user.duck';

/**
 * The admin overview.
 *
 * One read, and one write that stores nothing: everything on the page is
 * aggregated by our own server (`server/api/admin/overview.js`), which checks
 * the caller's admin role and holds the result for a few minutes — the
 * alternative is the browser making half a dozen paginated Integration API
 * queries on every visit. The business invite link is signed by the server
 * on request (`server/api/business-invite.js`) and lives only in the
 * response.
 */

export const fetchOverview = createAsyncThunk(
  'AdminOverviewPage/fetchOverview',
  async (_, { rejectWithValue }) => {
    try {
      return await getAdminOverview();
    } catch (e) {
      return rejectWithValue(storableError(e));
    }
  }
);

/**
 * A signed link that admits a business account on the signup page, made for
 * the address given (optional). See server/api/business-invite.js.
 */
export const createInvite = createAsyncThunk(
  'AdminOverviewPage/createInvite',
  async ({ email } = {}, { rejectWithValue }) => {
    try {
      return await createBusinessInvite({ email: email || null });
    } catch (e) {
      return rejectWithValue(storableError(e));
    }
  }
);

const adminOverviewPageSlice = createSlice({
  name: 'AdminOverviewPage',
  initialState: {
    overview: null,
    fetchInProgress: false,
    fetchError: null,
    // { url, email, expiresAt, validForDays } for the last link made, or null.
    invite: null,
    inviteInProgress: false,
    inviteError: null,
  },
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(createInvite.pending, state => {
        state.inviteInProgress = true;
        state.inviteError = null;
      })
      .addCase(createInvite.fulfilled, (state, action) => {
        state.inviteInProgress = false;
        state.invite = action.payload;
      })
      .addCase(createInvite.rejected, (state, action) => {
        state.inviteInProgress = false;
        state.inviteError = action.payload;
      })
      .addCase(fetchOverview.pending, state => {
        state.fetchInProgress = true;
        state.fetchError = null;
      })
      .addCase(fetchOverview.fulfilled, (state, action) => {
        state.fetchInProgress = false;
        state.overview = action.payload;
      })
      .addCase(fetchOverview.rejected, (state, action) => {
        state.fetchInProgress = false;
        state.fetchError = action.payload;
      });
  },
});

export default adminOverviewPageSlice.reducer;

/**
 * The current user is needed before the page can decide whether to show
 * itself at all, so it is fetched alongside rather than after.
 */
export const loadData = () => dispatch => {
  return Promise.all([dispatch(fetchCurrentUser()), dispatch(fetchOverview())]);
};
